import { AuthenticationTypes, Role,  User } from "@prisma/client";
import { includes } from "lodash";
import { any, z } from "zod";

;

export const LoginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1, "Enter Password")
});

export type LoginFormType = z.infer<typeof LoginSchema>;

export const UserSchema = z.object({
  id: z.string().optional().nullable(),
  name: z.string().min(1).max(30),
  email: z.string().email(),
  authenticationType: z
    .nativeEnum(AuthenticationTypes)
    .optional()
    .default(AuthenticationTypes.credentials),
  role: z.nativeEnum(Role).optional().default(Role.user),
  organization: z.string().min(1, "Organization name is must to identify").optional().nullable(),
});

export type UserType = z.infer<typeof UserSchema>;

export const UserCreateSchema = UserSchema.extend({
  password: z.string().min(6, "Min 6 Digit").max(12, "Max 12 Digit")
});

export type UserCreateType = z.infer<typeof UserCreateSchema>;

export const UserUpdateSchema = UserSchema.extend({
  password: z.string().max(12, "Max 12 Digit").optional()
});

export type UserUpdateType = z.infer<typeof UserUpdateSchema>;

export const PaginationSchema = z.object({
  page: z
    .number({ invalid_type_error: "Page must be a number" })
    .min(0, "Page must be 0 or greater")
    .optional()
    .default(1),
  pageSize: z
    .number({ invalid_type_error: "Page size must be a number" })
    .min(1, "Page size must be at least 1")
    .optional()
    .default(10)
});

export type PaginationType = z.infer<typeof PaginationSchema>;

export const SearchSchema = PaginationSchema.extend({
  keyword: z.string().min(0).optional().default(""),
  id: z.any().optional(),
  code: z.any().optional(),
  noUserVlan: z.boolean().optional().default(true),
  noUserVlanGroups: z.boolean().optional().default(true),
  onlyUserVlans: z.boolean().optional().default(false),
  onlyUserVlanGroups: z.boolean().optional().default(false),
  noPagination: z.boolean().optional().default(false),

  planName: z.string().optional(),

  noVlanPlan: z.boolean().optional().default(true),
  noVlanGroupPlan: z.boolean().optional().default(true),
  onlyVlanPlans: z.boolean().optional().default(false),
  onlyVlanGroupPlans: z.boolean().optional().default(false),
  vlanId: z.any().optional(),
  siteId: z.any().optional(),
  includePlans: z.boolean().optional().default(false),
  includeSites: z.boolean().optional().default(false),
  includeCP: z.boolean().optional().default(false),
  includeUser: z.boolean().optional().default(false),
  includeVlans: z.boolean().optional().default(false),
  includeVlanGroups: z.boolean().optional().default(false),

  range: z.string().optional().default("1w")
});

export type SearchSchemaType = z.infer<typeof SearchSchema>;

export const ActiveSessionSearchSchema = z.object({
  vlanId: z.any().optional(),
  userId: z.string().optional(),
  logged_in_start: z.string().optional(),
  logged_in_end: z.string().optional(),
  vlan: z
    .object({
      value: z.string().optional(),
      label: z.string().optional(),
      id: z.string().min(1)
    })
    .optional(),
  client_mac: z
    .string()
    .optional()
    .refine(
      (val) =>
        val === "" ||
        val == null ||
        /^([0-9A-Fa-f]{2}[:]){5}([0-9A-Fa-f]{2})$/.test(val),
      {
        message: "Invalid MAC address format"
      }
    ),
  client_ip: z
    .string()
    .optional()
    .refine(
      (val) =>
        val === "" ||
        val == null ||
        /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(
          val
        ),
      {
        message: "Invalid IP address format"
      }
    )
});

export type ActiveSessionSearchSchemaType = z.infer<
  typeof ActiveSessionSearchSchema
>;

export const VlanCreateSchema = z.object({
  id: z.string().min(1).optional(),
  ownerName: z.string().max(30).optional(),
  vlan: z.string().min(1).max(30),
  vlanName: z.string().min(1).max(30),
  description: z.string().max(30).optional(),
  GPSCoordinates: z.string().max(30).optional(),
  city: z.string().max(30).optional(),
  locationType: z.string().max(30).optional(),
  cp: z.any().optional(),
  site: z.object({
    value: z.string().optional(),
    label: z.string().optional(),
    id: z.string().min(1),
    name: z.string().optional()
  }),
  plans: z.array(z.any()).optional().default([])
});

export type VlanCreateType = z.infer<typeof VlanCreateSchema>;

export const VlanGroupCreateSchema = z.object({
  id: z.string().min(1).optional(),
  name: z.string().min(1).max(30),
  clientDetails: z.string().min(1).max(30),
  description: z.string().max(30).optional(),
  vlans: z.array(z.any()).optional().default([])
});

export type VlanGroupCreateType = z.infer<typeof VlanGroupCreateSchema>;

export const InitilizeAPISchema = z.object({
  name: z.string().trim().min(1, { message: "Name is required" }),
  email: z.string().trim().email({ message: "A valid email is required" }),
  password: z
    .string()
    .min(12, { message: "Password must be at least 12 characters" })
    .max(200, { message: "Password is too long" })
});

export const SessionSearchSchema = PaginationSchema.extend({
  fromDate: z
    .string({ required_error: "From date is required" })
    .min(1, "From date cannot be empty")
    .optional(),
  toDate: z
    .string({ required_error: "To date is required" })
    .min(1, "To date cannot be empty")
    .optional(),
  vlan: z.any(),
  source: z.string().optional(),
  os: z.string().optional(),
  macAddress: z.string().optional(),
  ipAddress: z.string().optional(),
  localId: z.string().optional()
});

export type SessionSearchType = z.infer<typeof SessionSearchSchema>;

export const ExtendedSessionSearchSchema = SessionSearchSchema.extend({
  userId: z
    .string({
      required_error: "User ID is required",
      invalid_type_error: "User ID must be a string"
    })
    .min(1, "User ID cannot be empty"),
  name: z
    .string({
      required_error: "Name is required",
      invalid_type_error: "Name must be a string"
    })
    .min(1, "Name cannot be empty"),
  description: z.string().optional()
});

export type ExtendedSessionSearchSchemaType = z.infer<
  typeof ExtendedSessionSearchSchema
>;

export const AccountSearchSchema = z.object({
  userId: z.string().optional(),
  vlan: z
    .object({
      value: z.string().optional(),
      label: z.string().optional(),
      id: z.string().min(1)
    })
    .refine((v) => v?.id, { message: "VLAN ID is required", path: ["id"] }),
  plan: z
    .object({
      value: z.string().optional(),
      label: z.string().optional(),
      id: z.string().min(1)
    })
    .refine((p) => p?.label, {
      message: "Plan label is required",
      path: ["label"]
    }),
  dateRange: z
    .object({
      startDate: z.any(),
      endDate: z.any()
    })
    .partial()
    .optional()
});

export type AccountSearchSchemaType = z.infer<typeof AccountSearchSchema>;

export const AccountExportSchema = z
  .object({
    userId: z.string().optional(),

    site: z
      .object({
        value: z.string().optional(),
        label: z.string().optional(),
        id: z.string().min(1, "Site ID is required")
      })
      .optional(),

    vlan: z
      .object({
        value: z.string().optional(),
        label: z.string().optional(),
        id: z.string().min(1, "VLAN ID is required")
      })
      .optional(),

    plan: z
      .object({
        value: z.string().optional(),
        label: z.string().min(1, "Plan label is required"),
        id: z.string().min(1, "Plan ID is required")
      })
      .optional(),

    // Optional if cron is defined
    dateRange: z
      .object({
        startDate: z.any(),
        endDate: z.any()
      })
      .partial()
      .optional(),

    // Define cron schedule
    cronSchedule: z
      .object({
        type: z.enum(["hourly", "daily", "weekly", "monthly", "interval"]),
        intervalMinutes: z.number().positive().optional() // only for "interval"
      })
      .optional()
  })
  .superRefine((data, ctx) => {
    const hasSite = data.site?.id;
    const hasCron = !!data.cronSchedule;

    if (!hasSite) {
      if (!data.vlan?.id) {
        ctx.addIssue({
          path: ["vlan", "id"],
          code: z.ZodIssueCode.custom,
          message: "VLAN is required if Site is not selected"
        });
      }

      if (!data.plan?.id) {
        ctx.addIssue({
          path: ["plan", "id"],
          code: z.ZodIssueCode.custom,
          message: "Plan is required if Site is not selected"
        });
      }
    }

    // dateRange is required only if cronSchedule is not set
    if (!hasCron) {
      if (!data.dateRange?.startDate || !data.dateRange?.endDate) {
        ctx.addIssue({
          path: ["dateRange"],
          code: z.ZodIssueCode.custom,
          message:
            "Start and end date are required unless using a cron schedule"
        });
      }
    }

    // If interval type, intervalMinutes must be given
    if (
      data.cronSchedule?.type === "interval" &&
      !data.cronSchedule.intervalMinutes
    ) {
      ctx.addIssue({
        path: ["cronSchedule", "intervalMinutes"],
        code: z.ZodIssueCode.custom,
        message:
          "Interval minutes must be specified when using interval cron type"
      });
    }
  });

export type AccountExportSchemaType = z.infer<typeof AccountExportSchema>;

export const AccountCreateSchema = z.object({
  userid: z.string().min(1).max(30),
  password: z.string().min(1).max(30),
  description: z.string().min(0).optional(),
  sharing_max: z.string().min(1).max(30),
  phone: z.string().min(8).max(8),

  creator: z.string().optional(),
  type: z.string().optional(),
  count: z.string().optional(),
  enabled: z.string().optional(),
  add_transaction: z.string().optional(),
  vlan: z.object({
    value: z.string().optional(),
    label: z.string().optional(),
    id: z.string().min(1)
  }),
  plan: z.object({
    value: z.string().optional(),
    label: z.string().optional(),
    id: z.string().min(1)
  })
});
export type AccountCreateType = z.infer<typeof AccountCreateSchema>;

export const SMSSchema = z.object({
  tar_num: z.string(),
  tar_msg: z.string()
});
export type SMSSchemaType = z.infer<typeof SMSSchema>;

export const AccountPasswordChangeSchema = z.object({
  userid: z.any().optional(),
  code: z.any().optional(),
  password: z.string().min(1).max(30),
  description: z.any().optional(),
  phone: z.any().optional(),
  creator: z.any().optional(),
  vlanId: z.any()
});
export type AccountPasswordChangeType = z.infer<
  typeof AccountPasswordChangeSchema
>;

export const AccountDeleteSchema = z.object({
  userid: z.any().optional(),
  vlanId: any().optional()
});
export type AccountDeleteSchemaType = z.infer<typeof AccountDeleteSchema>;

export const ExportSearchSchema = PaginationSchema.extend({
  userId: z.any().optional()
});
export type ExportSearchSchemaType = z.infer<typeof ExportSearchSchema>;

const isNonEmpty = (val: any): boolean => {
  return (
    val !== null &&
    val !== undefined &&
    val !== "" &&
    (!Array.isArray(val) || val.length > 0)
  );
};

//Schemas Of CP
export const CPSchema = z
  .object({
    cpId: z.string(),
    logo: z.any(),
    bgType: z
      .enum(["image", "color"], {
        errorMap: () => ({
          message: "Background type must be either 'image' or 'color'"
        })
      })
      .optional(),

    bgColor: z.any().optional(),
    bgImage: z.any().optional(),

    btnColor: z.any().optional(),
    btnTexttColor: z.any().optional(),

    favicon: z.any(),
    plan: z.any().optional(),

    uae: z.object({
      isBlocked: z.boolean()
    }),

    authMethod: z.object({
      isOTP: z.boolean().default(false),
      isCredentials: z.boolean().default(false),
      isAccessCode: z.boolean().default(false)
    }),

    otp: z.object({
      otpLength: z
        .number()
        .min(4, { message: "OTP length must be at least 4 digits" })
        .max(10, { message: "OTP length must be at most 10 digits" }),
      rateLimitationOTP: z.object({
        enabled: z.boolean(),
        cooldownSeconds: z
          .number()
          .min(10, { message: "Cooldown must be at least 10 seconds" })
          .max(1000, { message: "Cooldown must be at most 1000 seconds" })
      })
    }),

    sliderImages: z.any().optional(),
    slider: z
      .object({
        enabled: z.boolean(),
        height: z
          .string()
          .regex(/^\d+px$/, { message: "Height must be like '200px'" })
          .optional(),
        interval: z
          .number()
          .min(10, { message: "Interval must be at least 1000 ms" })
          .max(60000, { message: "Interval must be at most 60000 ms" })
          .optional(),
        touch: z.boolean().optional(),
        images: z.array(z.any()).optional()
      })
      .superRefine((data, ctx) => {
        if (data.enabled) {
          if (!data.height) {
            ctx.addIssue({
              path: ["height"],
              code: z.ZodIssueCode.custom,
              message: "Height is required when slider is enabled"
            });
          }
          if (data.interval === undefined) {
            ctx.addIssue({
              path: ["interval"],
              code: z.ZodIssueCode.custom,
              message: "Interval is required when slider is enabled"
            });
          }
          if (data.touch === undefined) {
            ctx.addIssue({
              path: ["touch"],
              code: z.ZodIssueCode.custom,
              message: "Touch option is required when slider is enabled"
            });
          }
        }
      }),

    txt: z.object({
      eng: z.object({
        title: z.string(),
        secondaryTitle: z.string(),
        mobileNoFieldTitle: z.string(),
        getOtpBtnTxt: z.string(),
        otpnConfirmationBtnTxt: z.string(),
        alertMsgUAEBlocked: z.string(),
        accessCodeBtnText: z.string(),
        userIdFieldTitle: z.string(),
        passwordFieldTitle: z.string(),
        credentialsSubmitBTNTitle: z.string(),
        otpAlertMessage: z.string(),
        cooldownMessage: z.string(),
        errorEnterValidMobileNumer: z.string(),
        msgAfterOTPSent: z.string(),
        errorMsgIfOTPFailedToSend: z.string(),
        otpFieldPlaceHolder: z.string(),
        successPageTitle: z.string(),
        failPageTitle: z.string(),
        failePageDeaultErrorMsg: z.string(),
        failePageBtnTxt: z.string()
      }),
      ar: z.object({
        title: z.string(),
        secondaryTitle: z.string(),
        mobileNoFieldTitle: z.string(),
        getOtpBtnTxt: z.string(),
        otpnConfirmationBtnTxt: z.string(),
        alertMsgUAEBlocked: z.string(),
        accessCodeBtnText: z.string(),
        userIdFieldTitle: z.string(),
        passwordFieldTitle: z.string(),
        credentialsSubmitBTNTitle: z.string(),
        otpAlertMessage: z.string(),
        cooldownMessage: z.string(),
        errorEnterValidMobileNumer: z.string(),
        msgAfterOTPSent: z.string(),
        errorMsgIfOTPFailedToSend: z.string(),
        otpFieldPlaceHolder: z.string(),
        successPageTitle: z.string(),
        failPageTitle: z.string(),
        failePageDeaultErrorMsg: z.string(),
        failePageBtnTxt: z.string()
      })
    })
  })
  

export type CPSchemaType = z.infer<typeof CPSchema>;

export const CPCreateSchema = z.object({
  name: z.string().min(3).max(30),
  customer: z.string().min(3).max(40),
  description: z.string().max(200).optional(),
  template: z.string().min(3).max(10)
});

export type CPCreateSchemaType = z.infer<typeof CPCreateSchema>;

export const GenerateThumbnailBodySchema = z.object({
  htmlContent: z.string(),
  id: z.string()
});

export type GenerateThumbnailBodyType = z.infer<
  typeof GenerateThumbnailBodySchema
>;

// export const OrderCreateSchema = z.object({
//   id: z.string().optional().nullable(),
//   name: z.string().min(1).max(30),
//   email: z.string().email(),
//   authenticationType: z
//     .nativeEnum(AuthenticationTypes)
//     .optional()
//     .default(AuthenticationTypes.credentials),
//   role: z.nativeEnum(Role).optional().default(Role.user),
//   organization: z.string().min(1, "Organization name is must to identify").optional().nullable(),
// });

export const ReportsUISearchParamsSchema = z
  .object({
    vlan: z.string().nullable().optional(),
    vlanList: z
      .array(z.string()) // each string must not be empty
      .optional(),
    vlanGroup: z.string().nullable().optional(),
    from: z
      .string({
        required_error: "From date is missing",
        invalid_type_error: "From must be a string"
      })
      .refine((val) => !isNaN(Date.parse(val)), {
        message: "From date is invalid"
      }),
    to: z
      .string({
        required_error: "To date is missing",
        invalid_type_error: "To must be a string"
      })
      .refine((val) => !isNaN(Date.parse(val)), {
        message: "To date is invalid"
      })
  })
  .refine((data) => data.vlan || data.vlanGroup || data.vlanList, {
    message: "Either VLAN or VLAN Group or Vlan List must be provided",
    path: ["vlan"] // points to the field, can also be ["vlanGroup"] or general
  })
  .refine((data) => new Date(data.from) <= new Date(data.to), {
    message: "`From` date cannot be after `To` date",
    path: ["to"]
  });

export type SearchParamsType = z.infer<typeof ReportsUISearchParamsSchema>;
